import React, {createContext, useContext} from 'react';

import {envIsServerDeployment} from '../../config';
import {useViewer} from '../../state/viewer/hooks';
import {
  useFullScreenLinePlotMaxRunsDynamicConfig,
  useLinePlotMaxRunsDynamicConfig,
} from '../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useRampFlagFullFidelityRunsPerChart,
  useRampFlagIncreaseFullScreenMaxRuns,
} from '../../util/rampFeatureFlags/linePlotFeatureFlags';
import {type Key} from '../../util/runTypes';
import {useIsFullScreenPanel} from '../Panel/hooks/useIsFullScreenPanel';
import {parseExpressions} from '../PanelExpressionOptions';
import {DEFAULT_LINE_PLOT_SETTINGS} from '../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {
  DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS,
  DEFAULT_METRIC_LIMIT,
} from '../WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {getMaxRunsCeiling} from '../WorkspaceDrawer/Settings/runLinePlots/utils/getCascadingMaxRuns';
import {
  type ExcludeOutliersValues,
  type FullFidelityMode,
  isPointVizFullFidelity,
} from '../WorkspaceDrawer/Settings/types';
import {
  configKeysInExpression,
  summaryKeysInExpression,
} from './../../util/expr';
import {
  type AggregateCalculation,
  type PlotType,
} from './../../util/plotHelpers/types';
import {runsLinePlotConfigDefaults} from './defaults';
import {useRunsLinePlotSettings} from './RunsLinePlotContext/useRunsLinePlotSettings';
import {type DeltaQueryTypeOverride, type RunsLinePlotConfig} from './types';
import {isDatetimeAxisFormat} from './utils/isDatetimeAxisFormat';
import {resolveDeltaQueryTypeOverride} from './utils/resolveDeltaQueryTypeOverride';

export type PanelConfigContextType = {
  aggregateCalculations: AggregateCalculation[];
  getDefaultLegendTemplateByRunsetId: (runsetId: string | undefined) => string;
  entityName: string;
  excludeOutliers: ExcludeOutliersValues | undefined;
  expressionKeys: Key[];
  isFullFidelity: boolean;
  isMultiMetric: boolean;
  maxRunsLimit: number;
  metricLimit: number;
  parsedExpressions: ReturnType<typeof parseExpressions>;
  plotType: PlotType | undefined;
  projectName: string;
  xAxisIsDatetime: boolean;
  forceReactVis?: boolean;
  deltaQueryTypeOverride?: DeltaQueryTypeOverride;
  // Grouped-only opt-in, unset until chosen on this panel.
  groupedFullFidelityMode?: FullFidelityMode;
} & ReturnType<typeof useRunsLinePlotSettings>;

export const PanelConfigContext = createContext<PanelConfigContextType>({
  aggregateCalculations: [],
  getDefaultLegendTemplateByRunsetId: () => '',
  entityName: '',
  excludeOutliers: undefined,
  fullFidelityMode: undefined,
  groupedFullFidelityMode: undefined,
  expressionKeys: [],
  isFullFidelity: false,
  isMultiMetric: false,
  maxRunsLimit: DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS,
  metricLimit: DEFAULT_METRIC_LIMIT,
  parsedExpressions: {expressions: undefined, xExpression: undefined},
  plotType: undefined,
  projectName: '',
  xAxisIsDatetime: false,
  singlePointRenderMode: undefined,
  ...DEFAULT_LINE_PLOT_SETTINGS,
});

export const PanelConfigContextProvider = React.memo(
  ({
    children,
    config,
    data,
    getDefaultLegendTemplateByRunsetId,
    isFullFidelity,
  }: {
    children: React.ReactNode;
    config: RunsLinePlotConfig;
    data: {
      entityName: string;
      projectName: string;
    };
    getDefaultLegendTemplateByRunsetId: (
      runsetId: string | undefined
    ) => string;
    isFullFidelity: boolean;
  }) => {
    const {
      colorRunNames,
      coordinateZooming,
      displayFullRunName,
      excludeOutliers,
      fullFidelityMode,
      highlightedCompanionRunOnly,
      groupAgg,
      groupingDepth,
      limit,
      pointVisualizationMethod,
      showLegend,
      showXRange,
      smoothingType,
      smoothingWeight,
      tooltipNumberOfRuns,
      useRunsTableGroupingInPanels,
      xAxis,
      xAxisFormat,
      xAxisMax,
      xAxisMin,
      xLogScale,
      yAxisMax,
      yAxisMin,
      yLogScale,
      showOriginalAfterSmoothing,
      singlePointRenderMode,
    } = useRunsLinePlotSettings(config);

    const metricLimit = config.metricLimit ?? DEFAULT_METRIC_LIMIT;

    const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
    const canIncreaseFullScreenMaxRuns = useRampFlagIncreaseFullScreenMaxRuns();
    const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();
    const dynamicConfigFullScreenMaxRuns =
      useFullScreenLinePlotMaxRunsDynamicConfig();
    const {isFullScreen} = useIsFullScreenPanel();

    const maxRunsLimit = getMaxRunsCeiling({
      canOverrideMaxRuns,
      isBucketing: isPointVizFullFidelity(pointVisualizationMethod),
      isFullScreen,
      canIncreaseFullScreenMaxRuns,
      isLinePlot: true,
      dynamicConfigMaxRuns,
      dynamicConfigFullScreenMaxRuns,
    });

    // LB: TODO Make this come from legend spec
    const aggregateCalculations = React.useMemo(() => {
      return [
        {
          isShown: true,
          value: 'minmax',
        },
        {
          isShown: true,
          value: 'stddev',
        },
        {
          isShown: config.groupArea === 'stderr',
          value: 'stderr',
        },
        {
          isShown: config.groupArea === 'samples',
          value: 'samples',
        },
      ]
        .filter(ac => ac.isShown)
        .map(ac => ac.value as AggregateCalculation);
    }, [config.groupArea]);

    const parsedExpressions = React.useMemo(
      () => parseExpressions(config.expressions, config.xExpression),
      [config.expressions, config.xExpression]
    );

    // extra variables to collect for expressions
    const allExpressions = React.useMemo(() => {
      return [
        ...(parsedExpressions.expressions ?? []),
        ...(parsedExpressions.xExpression
          ? [parsedExpressions.xExpression]
          : []),
      ];
    }, [parsedExpressions.expressions, parsedExpressions.xExpression]);

    const expressionKeys = React.useMemo(() => {
      return allExpressions.flatMap(expr => [
        ...configKeysInExpression(expr),
        ...summaryKeysInExpression(expr),
      ]);
    }, [allExpressions]);

    const xAxisIsDatetime = isDatetimeAxisFormat(xAxis, xAxisFormat);

    const isMultiMetric = config.metrics != null && config.metrics.length >= 2;
    const plotType = config.plotType ?? runsLinePlotConfigDefaults.plotType;
    const viewer = useViewer();

    const forceReactVis =
      !envIsServerDeployment && viewer?.admin && config.forceReactVis === true;

    const deltaQueryTypeOverride = resolveDeltaQueryTypeOverride(
      config.deltaQueryTypeOverride,
      viewer?.admin,
      envIsServerDeployment
    );

    const value = React.useMemo<PanelConfigContextType>(
      () => ({
        aggregateCalculations,
        colorRunNames,
        coordinateZooming,
        getDefaultLegendTemplateByRunsetId,
        displayFullRunName,
        entityName: data.entityName,
        excludeOutliers,
        expressionKeys,
        forceReactVis,
        deltaQueryTypeOverride,
        fullFidelityMode,
        groupedFullFidelityMode: config.groupedFullFidelityMode,
        groupAgg,
        groupingDepth,
        highlightedCompanionRunOnly,
        isFullFidelity,
        isMultiMetric,
        limit,
        maxRunsLimit,
        metricLimit,
        parsedExpressions,
        plotType,
        pointVisualizationMethod,
        projectName: data.projectName,
        showLegend,
        showXRange,
        smoothingType,
        smoothingWeight,
        tooltipNumberOfRuns,
        useRunsTableGroupingInPanels,
        xAxis,
        xAxisFormat,
        xAxisIsDatetime,
        xAxisMax,
        xAxisMin,
        xLogScale,
        yAxisMax,
        yAxisMin,
        yLogScale,
        showOriginalAfterSmoothing,
        singlePointRenderMode,
      }),
      [
        aggregateCalculations,
        colorRunNames,
        coordinateZooming,
        data.entityName,
        data.projectName,
        getDefaultLegendTemplateByRunsetId,
        displayFullRunName,
        excludeOutliers,
        expressionKeys,
        forceReactVis,
        deltaQueryTypeOverride,
        fullFidelityMode,
        config.groupedFullFidelityMode,
        groupAgg,
        groupingDepth,
        highlightedCompanionRunOnly,
        isFullFidelity,
        isMultiMetric,
        limit,
        maxRunsLimit,
        metricLimit,
        parsedExpressions,
        plotType,
        pointVisualizationMethod,
        showLegend,
        showXRange,
        smoothingType,
        smoothingWeight,
        tooltipNumberOfRuns,
        useRunsTableGroupingInPanels,
        xAxis,
        xAxisFormat,
        xAxisIsDatetime,
        xAxisMax,
        xAxisMin,
        xLogScale,
        yAxisMax,
        yAxisMin,
        yLogScale,
        showOriginalAfterSmoothing,
        singlePointRenderMode,
      ]
    );
    return (
      <PanelConfigContext.Provider value={value}>
        {children}
      </PanelConfigContext.Provider>
    );
  }
);
PanelConfigContextProvider.displayName = 'PanelConfigContextProvider';

export const usePanelConfigContext = () => {
  return useContext(PanelConfigContext);
};
